'use-client';
import * as React from 'react';
import {
  formatClassNames,
  sanitizeHTML,
  getDataAttributes,
  getTabIndexAttribute,
} from '@wix/editor-elements-common-utils';
import { useResizeObserver } from '@wix/thunderbolt-elements/src/providers/useResizeObserver';
import classNames from 'clsx';
import { getLayoutClass } from '../../../CollapsibleText.utils';
import type {
  Classes,
  ICollapsibleTextCompProps,
  ExpandButtonComponentProps,
} from '../../../CollapsibleText.types';
import {
  FALLBACK_LINE_HEIGHT,
  ExpandModeValues,
  TestIds,
} from '../../../constants';
import { TagValue } from '../../../../../common/panel-sections/seoAndA11y/consts';
import semanticClassNames from '../../../CollapsibleText.semanticClassNames';
import { isSafariVersionLess } from '../../utils/isSafariVersionLess';
import styles from './CollapsibleText.module.scss';
import extendedStyles from './extendedStyleApi.module.scss';
import { ExpandControl } from './ExpandControl/ExpandControl';

/* line clamp doens't work properly with block items in Safari version less 17 */
const safariGoodVersion = 17;

const isRichText = (text: string) => /<\/?[a-z][\s\S]*>/i.test(text);

const noop = () => {};

const getIsExpandRedundant = (
  textRef: React.RefObject<HTMLDivElement>,
  maxLinesFromProps: number,
  isFromManifest: boolean,
  extendedStyleApi?: boolean,
) => {
  const txtElement = textRef.current;
  if (txtElement) {
    const computedStyle = getComputedStyle(txtElement);
    const lineHeight = computedStyle.lineHeight;
    let lineHeightAsNumber = parseFloat(lineHeight);

    const isBuilder = extendedStyleApi === false;
    const isValidLineHeightNumber = isBuilder
      ? Number.isInteger(Math.ceil(lineHeightAsNumber))
      : Number.isInteger(lineHeight);
    // Calculating based on the font when the line-height isn't a number (f.e when it's "normal"/"unset")
    if (!isValidLineHeightNumber) {
      if (isBuilder) {
        lineHeightAsNumber = FALLBACK_LINE_HEIGHT; // use the default line height for builder (1.4em) in case the line height is missing.
      } else {
        const fontSize = parseFloat(computedStyle.fontSize);
        const fallbackRatio = 1.5;
        lineHeightAsNumber = fontSize * fallbackRatio;
      }
    }

    // TODO: JB: maybe check for NaN
    const maxLinesFromCss = parseInt(
      computedStyle.getPropertyValue('--max-lines'),
      10,
    );

    const maxLines = !isFromManifest ? maxLinesFromProps : maxLinesFromCss;

    const maxHeight = Math.ceil(lineHeightAsNumber * maxLines);

    return txtElement.scrollHeight <= maxHeight;
  }

  return false;
};

const CollapsibleText: React.FC<
  ICollapsibleTextCompProps & Classes & ExpandButtonComponentProps
> = props => {
  const {
    id,
    text,
    className,
    customClassNames = [],
    direction,
    showExpandControl,
    expandMode,
    ellipsis,
    wrappingStyle,
    isExpanded,
    maxLines,
    lang,
    tag: TagName = TagValue.P,
    extendedStyleApi = true,
    onClick = noop,
    onDblClick = noop,
    onMouseEnter = noop,
    onMouseLeave = noop,
    withLogicalLayout = false,
    classes,
    textDirection,
    buttonDirection,
  } = props;
  const isManifest = !!classes;
  const textRef = React.useRef<HTMLDivElement>(null);
  const [isExpandRedundant, setIsExpandRedundant] = React.useState(false);
  const [shouldShowExpandControl, setShouldShowExpandControl] =
    React.useState(false);

  const textFormat = React.useMemo(() => {
    return isRichText(text) ? 'rtf' : 'plain';
  }, [text]);

  useResizeObserver({
    ref: textRef,
    callback: () =>
      setIsExpandRedundant(
        getIsExpandRedundant(textRef, maxLines, isManifest, extendedStyleApi),
      ),
  });

  React.useEffect(() => {
    setIsExpandRedundant(
      getIsExpandRedundant(textRef, maxLines, isManifest, extendedStyleApi),
    );
  }, [textRef, text, maxLines, isManifest, extendedStyleApi]);

  React.useEffect(() => {
    const shouldShow =
      ellipsis &&
      ((expandMode === ExpandModeValues.Link && showExpandControl) ||
        (expandMode === ExpandModeValues.Expand &&
          (isExpanded || !isExpandRedundant)));

    setShouldShowExpandControl(shouldShow);
  }, [ellipsis, expandMode, showExpandControl, isExpanded, isExpandRedundant]);

  const textClassName = classNames(
    styles.text,
    formatClassNames(semanticClassNames.text),
    {
      [styles[getLayoutClass(withLogicalLayout)]]: true,
      [styles.collapsed]: ellipsis && !isExpanded,
      [styles['single-line']]: wrappingStyle === 'single-line',
      [styles['multi-line']]: wrappingStyle === 'multi-line',
      [styles.plain]: textFormat === 'plain',
      [styles.withSafariListsFix]:
        wrappingStyle === 'multi-line' &&
        isSafariVersionLess(safariGoodVersion),
      [extendedStyles.text]: extendedStyleApi,
    },
    classes?.text,
  );

  // Needed for rich-text support
  const Text: React.FC<{ _text: string }> = ({ _text }) =>
    React.createElement(TagName, {
      dangerouslySetInnerHTML: {
        __html: sanitizeHTML(_text?.trim() || ''),
      },
      className: textClassName,
      ref: textRef,
    });

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      {...getTabIndexAttribute(props.a11y)}
      className={className}
      lang={lang}
      onClick={onClick}
      onDoubleClick={onDblClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        dir={!isManifest ? direction : undefined}
        className={classNames(
          styles.root,
          formatClassNames(semanticClassNames.root, ...customClassNames),
          classes?.root,
          {
            [extendedStyles.root]: extendedStyleApi,
          },
        )}
        data-testid={TestIds.viewer.root}
      >
        <div
          dir={isManifest ? textDirection : undefined}
          data-testid={TestIds.viewer.text_wrapper}
          className={styles['text-wrapper']}
        >
          <Text _text={text} />
        </div>
        {shouldShowExpandControl && (
          <div
            dir={isManifest ? buttonDirection : undefined}
            className={classNames(
              styles['button-wrapper'],
              extendedStyles['button-wrapper'],
              classes?.buttonWrapper,
            )}
          >
            <ExpandControl {...props} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CollapsibleText;
